import React, { useEffect, useMemo, useState } from "react";
import { ApolloProvider, useApolloClient, useQuery } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import { Alert, Box } from "@mui/material";
import { createApolloClient } from "../../apis/apolloClient";
import {
  GET_TRUST_CENTER,
  GET_TRUST_CENTER_DOWNLOAD,
} from "../../apis/trust-center-apis/trustCenterApis";
import { toPublicView } from "./data/adminView";
import TrustCenterPage from "./TrustCenterPage";

// Shows the signed-in admin what visitors will see, including before the
// page is published. `null` while loading keeps the page's skeleton up.
const PreviewData = () => {
  const client = useApolloClient();
  const { data, error, refetch } = useQuery(GET_TRUST_CENTER, {
    fetchPolicy: "network-only",
  });
  const preview = useMemo(
    () => (data?.getTrustCenter ? toPublicView(data.getTrustCenter) : null),
    [data]
  );
  if (error) {
    return (
      <Box sx={{ maxWidth: 720, mx: "auto", p: 4 }}>
        <Alert severity="error">
          Could not load the preview. Only admins can preview the Trust Center.
        </Alert>
      </Box>
    );
  }
  // Any resource the admin can see, through the signed-in API.
  const download = async ({
    resourceId = null,
    resourceIds = null,
    view = false,
  }) => {
    try {
      const result = await client.query({
        query: GET_TRUST_CENTER_DOWNLOAD,
        variables: { resourceId, resourceIds, view },
        fetchPolicy: "no-cache",
      });
      return result.data.getTrustCenterDownload;
    } catch (err) {
      throw new Error(err.graphQLErrors?.[0]?.message || err.message);
    }
  };
  return (
    <TrustCenterPage
      preview={preview}
      onPreviewRefresh={refetch}
      onPreviewDownload={download}
    />
  );
};

// /trust/<slug>?preview=1. Rendered inside Auth0Provider (see src/index.js).
const TrustCenterPreview = () => {
  const {
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
    getIdTokenClaims,
    loginWithRedirect,
  } = useAuth0();
  const [client, setClient] = useState(null);

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      loginWithRedirect({
        appState: {
          returnTo: window.location.pathname + window.location.search,
        },
      });
      return;
    }
    (async () => {
      const accessToken = await getAccessTokenSilently();
      const idToken = (await getIdTokenClaims()).__raw;
      setClient(createApolloClient(accessToken, idToken));
    })().catch((e) => console.error("Trust Center preview:", e));
  }, [
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
    getIdTokenClaims,
    loginWithRedirect,
  ]);

  if (!client) return <TrustCenterPage preview={null} />;
  return (
    <ApolloProvider client={client}>
      <PreviewData />
    </ApolloProvider>
  );
};

export default TrustCenterPreview;
